import {
  AppointmentDialogSlot
} from '@/components/appointments/extensions/extension-types';
import type {
  ExtendedModuleAppointmentExtension,
  AppointmentExtensionContext,
  AppointmentExtensionValidation,
  AppointmentContextData,
  AppointmentDetailAction,
  AppointmentCreationInterception
} from '@/components/appointments/extensions/extension-types';
import { AIAutomatedCreation } from '../components/appointment/AIAutomatedCreation';
import type { JobModuleApplication } from '../api';
import { callWebhook } from '../services/http-client';
import { Appointment } from '@/api/mcp-appointments';
import { buildApplicationEventHelpers } from './buildApplicationEventHelpers';

/**
 * Job Module Appointment Extension
 *
 * Creation Dialog:
 * - Builds context with application data (stored in appointment.context)
 * - Adds AI Automated Creation toggle (after notes section)
 * - Calls webhook when AI Automated Creation is enabled
 *
 * Display Extensions:
 * - Custom calendar labels/tooltips/details for interview application linkage
 * - Meeting format/links come from native appointment fields
 */
export const jobModuleAppointmentExtension: ExtendedModuleAppointmentExtension = {
  moduleId: 'job-module',

  getSlotComponents: (slot: AppointmentDialogSlot, _context: AppointmentExtensionContext) => {
    if (slot === AppointmentDialogSlot.AFTER_NOTES) {
      return <AIAutomatedCreation />;
    }

    return null;
  },

  buildContext: (
    _formValues: Record<string, any>,
    moduleData?: any
  ): AppointmentContextData | null => {
    const application = moduleData?.application as JobModuleApplication | undefined;

    if (!application) {
      return null;
    }

    return {
      source: 'job-module',
      applicationId: application.id,
      applicationName: application.name,
      applicationEmail: application.email,
      position: application.position
    };
  },

  validate: async (
    _formValues: Record<string, any>,
    _moduleData?: any
  ): Promise<AppointmentExtensionValidation> => {
    return { valid: true };
  },

  getInitialValues: (_moduleData?: any): Record<string, any> => {
    return {};
  },

  onBeforeAppointmentCreated: async (
    formValues: Record<string, any>,
    appointmentData: any,
    moduleData?: any
  ): Promise<AppointmentCreationInterception> => {
    const application = moduleData?.application as JobModuleApplication | undefined;

    // Always create an appointment - set status based on AI Automated Creation
    if (application && formValues.aiAutomatedCreation) {
      // Set status to SHADOW when AI Automated Creation is enabled
      appointmentData.status = 'SHADOW';
    } else {
      // Set status to SCHEDULED when AI Automated Creation is not enabled
      // (or let it default to SCHEDULED if status is not set)
      appointmentData.status = appointmentData.status || 'SCHEDULED';
    }

    // Always proceed with creation (never skip)
    return {}; // Empty object means proceed with creation
  },

  onAppointmentCreated: async (appointment: any, formValues: Record<string, any>, moduleData?: any) => {
    const application = moduleData?.application as JobModuleApplication | undefined;

    if (!application) {
      return;
    }

    console.log('Job module: Appointment created for application', application.id);

    // If AI Automated Creation is enabled, call webhook with appointment ID and status
    // The agent can find the appointment using the ID and status (SHADOW)
    if (formValues.aiAutomatedCreation && appointment.status === 'SHADOW') {
      const webhookBody = {
        appointment: appointment,
        application: application,
        communicationChannel: formValues.aiCommunicationChannel || 'email',
        additionalNotes: formValues.aiAdditionalNotes || undefined,
      };

      try {
        await callWebhook(webhookBody);
        console.log('Job module: Webhook called successfully for AI Automated Creation', application.id);
        console.log('Job module: Appointment ID sent to webhook:', appointment.id);
      } catch (error) {
        console.error('Job module: Failed to call webhook for AI Automated Creation', error);
        // Don't throw - appointment is already created, webhook failure shouldn't fail the whole operation
      }
    }
  },

  onAppointmentError: async (error: Error, _formValues: Record<string, any>, _moduleData?: any) => {
    console.error('Job module: Failed to create appointment', error);
  },

  onAppointmentCancelled: (_formValues: Record<string, any>, _moduleData?: any) => {
    console.log('Job module: Appointment creation cancelled');
  },

  // ========== Display Extension Methods ==========

  ...buildApplicationEventHelpers({
    sectionId: 'interview-info',
    title: 'position',
    name: 'applicationName',
    email: 'applicationEmail'
  }),

  /**
   * Custom action buttons for appointment details popup
   */
  getDetailActions: (_appointment: Appointment, _contextData: Record<string, any>): AppointmentDetailAction[] => {
    return [];
  }
};
